import {
  PaginatedAPIRequest,
  PaginationOptions,
  PaginatedAPIData,
  emptyPaginatedAPIData,
  LoadingPaginatedAPIData,
  FilledPaginatedAPIData,
  loadingPaginatedAPIData,
  getResultsCount,
  PaginatedAPIResult,
  IndirectPaginationOptions,
  PaginationAPIActionGenerator,
  IndirectPaginatedAPIData,
  emptyIndirectPaginatedAPIData,
} from './common';
import {
  APIActionGenerator,
  APIAction,
  loadingResults,
  emptyResults,
  storeResults,
  FilledAPIData,
} from '../apidata';
import { APIResult } from '../api';

export function paginationStore<
  R extends PaginatedAPIRequest<T, E>,
  T = unknown,
  P = unknown,
  E = unknown
>(
  opts: PaginationOptions<R, T, P, E>,
  generator: APIActionGenerator<R, T, E>,
  state: PaginatedAPIData<P, E>,
  action: APIAction<R, T, E>
): PaginatedAPIData<P, E> {
  const { types } = generator;
  switch (action.type) {
    case types.clear: {
      return emptyPaginatedAPIData;
    }

    case types.request: {
      const { request } = action;
      if (request === undefined) {
        return state;
      }
      return handleLoadingPage(opts, state, request);
    }

    case types.failure:
    case types.success: {
      const { request, response } = action;
      if (request === undefined || response === undefined) {
        return state;
      }

      return handlePageResponse(opts, state, request, response);
    }

    default: {
      return state;
    }
  }
}

/** Take paginated state and apply a loading page to it */
export function handleLoadingPage<
  R extends PaginatedAPIRequest<T, E>,
  T,
  E,
  P = unknown
>(
  opts: PaginationOptions<R, T, P, E>,
  state: PaginatedAPIData<P, E>,
  request: R
): LoadingPaginatedAPIData | FilledPaginatedAPIData<P, E> {
  // If our state isn't yet "filled", go ahead and set the "loading" results.
  if (!state.isFilled) {
    return loadingPaginatedAPIData;
  }

  // Once the state is filled, we can go ahead and add the query and set its
  // pageID to "loading" if it's not yet been set. If it has, keep the previous
  // result so that there's no state "stutter" and the previous results exist
  // right up until the new results are loaded or an error is encountered. If
  // there are no previous results, then indicate that this page is loading.
  const pageID = opts.getPageID
    ? opts.getPageID(request)
    : defaultGetPageID<R, T, E>(request);
  const { pageResults, previousPage, nextPage } = state;
  const newPage = pageResults[pageID] ?? loadingResults;
  let pageQueries = state.pageQueries;
  if (pageQueries.length === 0) {
    pageQueries = [pageID];
  } else if (pageID === previousPage) {
    pageQueries = [pageID, ...pageQueries];
  } else if (pageID === nextPage) {
    pageQueries = [...pageQueries, pageID];
  }

  return {
    ...state,
    pageQueries,
    pageResults: {
      ...pageResults,
      [pageID]: newPage,
    },
  };
}

/** Take paginated state and apply a completed result to it */
export function handlePageResponse<
  R extends PaginatedAPIRequest<T, E>,
  T,
  P,
  E = unknown
>(
  opts: PaginationOptions<R, T, P, E>,
  state: PaginatedAPIData<P, E>,
  request: R,
  result: APIResult<T, E>
): FilledPaginatedAPIData<P, E> {
  const pageID = opts.getPageID
    ? opts.getPageID(request)
    : defaultGetPageID<R, T, E>(request);
  const { reset } = request;
  let newPageQueries = state.isFilled ? state.pageQueries : [];
  let oldPageResults = state.isFilled ? state.pageResults : {};
  const existingPage = oldPageResults[pageID] ?? emptyResults;
  const newPage = generateNewPage(opts, result);
  let nextPage = state.isFilled ? state.nextPage : undefined;
  let previousPage = state.isFilled ? state.previousPage : undefined;
  let resultsCount = getResultsCount(state);

  // First, we have to subtract out the old page count. If we're resetting the
  // pages, then we just set this value to 0 and clear out the old results as
  // we're starting fresh.
  if (!reset && existingPage && existingPage.isFilled && existingPage.success) {
    resultsCount -= existingPage.data.results.length;
  } else if (reset) {
    resultsCount = 0;
    oldPageResults = {};
    newPageQueries = [];
  }

  const { success } = newPage;
  if (success) {
    const { data } = newPage;

    // Add in the new results
    resultsCount += data.results.length;

    if (newPageQueries.length === 0) {
      // If there are no pages, or if we're resetting the results, we can set
      // both previous and next accordingly as well as add our page as the only
      // one.
      previousPage = data.previousPage;
      nextPage = data.nextPage;
      newPageQueries = [pageID];
    } else if (newPageQueries[newPageQueries.length - 1] === pageID) {
      // If our result is the last element, we can use it to set the next page
      // data.
      nextPage = data.nextPage;
    } else if (newPageQueries[0] === pageID) {
      // Conversely, if it's the first, set the previous page in case we're
      // iterating backwards.
      previousPage = data.previousPage;
    }

    return {
      isLoading: false,
      isFilled: true,
      isEmpty: false,

      pageQueries: newPageQueries,
      pageResults: {
        ...oldPageResults,
        [pageID]: newPage,
      },
      resultsCount,
      success,
      previousPage,
      nextPage,
    };
  }

  // Add in the errored data if the request wasn't successful.
  const { errorData } = newPage;
  return {
    isLoading: false,
    isFilled: true,
    isEmpty: false,

    errorData,
    pageQueries: newPageQueries,
    pageResults: {
      ...oldPageResults,
      [pageID]: newPage,
    },
    resultsCount,
    success,
    previousPage,
    nextPage,
  };
}

export function defaultGetPageID<
  R extends PaginatedAPIRequest<T, E>,
  T,
  E = unknown
>(request: R): string {
  return request.page || '';
}

/** Generate a new page of data from the given result */
export function generateNewPage<
  R extends PaginatedAPIRequest<T, E>,
  T,
  P,
  E = unknown
>(
  opts: PaginationOptions<R, T, P, E>,
  result: APIResult<T, E>
): FilledAPIData<PaginatedAPIResult<P>, E> {
  const { translatePage } = opts;
  if (!result.success) {
    return storeResults(result);
  }
  return storeResults({
    ...result,
    data: translatePage(result.data),
  });
}

/**
 * Query paginated result entry data by the given index
 *
 * @param paginatedData - page data structure
 * @param index - index to query
 */
export function getEntryByIndex<P, E = unknown>(
  paginatedData: PaginatedAPIData<P, E>,
  index: number
): P | undefined {
  if (index < 0 || !paginatedData.isFilled) {
    return undefined;
  }

  const { pageQueries } = paginatedData;
  const { pageResults } = paginatedData;
  let pageOffset = 0;
  for (const url of pageQueries) {
    const pageResult = pageResults[url];

    // If a page is loading, then we're not yet ready to show its data.
    // Continue on to the next page.
    if (pageResult.isLoading) {
      continue;
    }

    if (!pageResult.isFilled || !pageResult.success) {
      return undefined;
    }

    const { data } = pageResult;
    const nextOffset = pageOffset + data.results.length;
    if (index < nextOffset) {
      return data.results[index - pageOffset];
    }
    pageOffset = nextOffset;
  }
  return undefined;
}

export function indirectPaginationStore<
  R extends PaginatedAPIRequest<T, E>,
  T = unknown,
  P = unknown,
  E = unknown
>(
  opts: IndirectPaginationOptions<R, T, P, E>,
  generator: PaginationAPIActionGenerator<R, T, P, E>,
  state: IndirectPaginatedAPIData<P, E>,
  action: APIAction<R, T, E>
): IndirectPaginatedAPIData<P, E> {
  const { types } = generator;
  const store = generator.store();
  switch (action.type) {
    case types.clear: {
      const { request } = action;
      if (request === undefined) {
        return emptyIndirectPaginatedAPIData;
      }

      const { getKey } = opts;
      const key = getKey(request);
      const paginatedAPIData = state[key] || emptyPaginatedAPIData;
      return {
        ...state,
        [key]: store(paginatedAPIData, action),
      };
    }

    case types.request:
    case types.failure:
    case types.success: {
      const { request } = action;
      if (request === undefined) {
        return state;
      }

      const { getKey } = opts;
      const key = getKey(request);
      const paginatedAPIData = state[key] || emptyPaginatedAPIData;
      return {
        ...state,
        [key]: store(paginatedAPIData, action),
      };
    }

    default: {
      return state;
    }
  }
}

/**
 * Query all paginated results as an array
 *
 * @param paginatedData - page data structure
 */
export function listEntries<P>(paginatedData: PaginatedAPIData<P, any>): P[] {
  if (!paginatedData.isFilled) {
    return [];
  }

  const results: P[] = [];
  const { pageQueries } = paginatedData;
  const { pageResults } = paginatedData;
  for (const url of pageQueries) {
    const pageResult = pageResults[url];

    // If we reach a page without valid data, go ahead and bail out. We can't
    // really trust any subsequent data if there is any.
    if (!pageResult.isFilled || !pageResult.success) {
      break;
    }

    const { data } = pageResult;
    results.push(...data.results);
  }
  return results;
}

/**
 * Check to see if the given paginated data has any loading pages
 *
 * @param paginatedData - page data structure
 */
export function hasLoadingPage(
  paginatedData: PaginatedAPIData<any, any>
): boolean {
  if (paginatedData.isLoading) {
    return true;
  }
  if (!paginatedData.isFilled) {
    return false;
  }

  const { pageResults } = paginatedData;
  for (const pageResult of Object.values(pageResults)) {
    if (pageResult.isLoading) {
      return true;
    }
  }
  return false;
}
